import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const StatsRecordingForm = ({
  playerId,
  onSuccess,
  organizationId,
  // Legacy props for backward compatibility
  player,
  onClose,
  coachId,
  isMobile = false,
}) => {
  const effectivePlayerId = playerId || player?.id;
  const effectiveOnSuccess = onSuccess || onClose;

  const [statCategories, setStatCategories] = useState([]);
  const [stats, setStats] = useState([]);
  const [recordedDate, setRecordedDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState('');

  // Load stat categories for the organization
  useEffect(() => {
    const loadStatCategories = async () => {
      if (!organizationId) {
        setCategoriesLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('stat_categories')
          .select('*')
          .eq('organization_id', organizationId)
          .order('display_order')
          .order('name');

        if (error) throw error;

        setStatCategories(data || []);

        // Initialize with first category if available
        if (data && data.length > 0) {
          setStats([
            {
              stat_category_id: data[0].id,
              value: '',
              unit: data[0].unit,
            },
          ]);
        }
      } catch (err) {
        console.error('Error loading stat categories:', err);
        setError('Failed to load stat categories');
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadStatCategories();
  }, [organizationId]);

  const addStatField = () => {
    if (statCategories.length > 0) {
      setStats([
        ...stats,
        {
          stat_category_id: statCategories[0].id,
          value: '',
          unit: statCategories[0].unit,
        },
      ]);
    }
  };

  const removeStatField = (index) => {
    if (stats.length > 1) {
      setStats(stats.filter((_, i) => i !== index));
    }
  };

  const updateStat = (index, field, value) => {
    const newStats = [...stats];
    newStats[index][field] = value;

    if (field === 'stat_category_id') {
      const category = statCategories.find((cat) => cat.id === value);
      if (category) {
        newStats[index].unit = category.unit;
      }
    }

    setStats(newStats);
  };

  const validateForm = () => {
    const validStats = stats.filter((stat) => stat.value !== '');

    if (validStats.length === 0) {
      setError('Please enter at least one stat value');
      return false;
    }

    // Check for negative values
    const hasNegative = validStats.some((stat) => parseFloat(stat.value) < 0);
    if (hasNegative) {
      setError('Stats cannot be negative');
      return false;
    }

    // Date validation
    const selectedDate = new Date(recordedDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (selectedDate > today) {
      setError('Date cannot be in the future');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const validStats = stats.filter((stat) => stat.value !== '');

      const statRecords = validStats.map((stat) => ({
        player_id: effectivePlayerId,
        stat_category_id: stat.stat_category_id,
        value: parseFloat(stat.value),
        unit: stat.unit,
        recorded_date: recordedDate,
        recorded_by: coachId,
        notes: notes || null,
      }));

      const { error: insertError } = await supabase.from('player_stats').insert(statRecords);

      if (insertError) throw insertError;

      // Call success callback after delay
      if (effectiveOnSuccess) {
        setTimeout(() => {
          effectiveOnSuccess();
        }, 1500);
      }
    } catch (error) {
      console.error('Error recording stats:', error);
      setError(error.message || 'Failed to save stats');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      width: '100%',
      boxSizing: 'border-box',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '100%',
      boxSizing: 'border-box',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333',
    },
    input: {
      padding: '10px',
      fontSize: isMobile ? '16px' : '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      width: '100%',
      boxSizing: 'border-box',
    },
    select: {
      padding: '10px',
      fontSize: isMobile ? '16px' : '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      backgroundColor: 'white',
      width: '100%',
      boxSizing: 'border-box',
    },
    textarea: {
      padding: '10px',
      fontSize: isMobile ? '16px' : '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      minHeight: '80px',
      resize: 'vertical',
      width: '100%',
      boxSizing: 'border-box',
    },
    statRow: isMobile
      ? {
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e0e0e0',
          width: '100%',
          boxSizing: 'border-box',
        }
      : {
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr auto',
          gap: '10px',
          alignItems: 'flex-end',
          width: '100%',
          boxSizing: 'border-box',
        },
    statsList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      width: '100%',
      boxSizing: 'border-box',
    },
    mobileFieldLabel: {
      fontSize: '12px',
      fontWeight: '500',
      color: '#666',
      marginBottom: '4px',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #fcc',
    },
    button: {
      padding: isMobile ? '14px 20px' : '10px 20px',
      fontSize: '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: isMobile ? '44px' : 'auto',
      width: isMobile ? '100%' : 'auto',
    },
    smallButton: {
      padding: '8px 12px',
      fontSize: '12px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    primaryButton: {
      backgroundColor: '#007bff',
      color: 'white',
    },
    successButton: {
      backgroundColor: '#28a745',
      color: 'white',
    },
    dangerButton: {
      backgroundColor: '#dc3545',
      color: 'white',
    },
    addButton: {
      marginTop: '10px',
      alignSelf: 'flex-start',
    },
  };

  if (categoriesLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading stat categories...</div>;
  }

  if (statCategories.length === 0) {
    return (
      <div style={styles.error}>
        No stat categories available. Please contact your administrator to set up stat categories
        for your organization.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="recorded-date">
          Recorded Date *
        </label>
        <input
          id="recorded-date"
          type="date"
          style={styles.input}
          value={recordedDate}
          onChange={(e) => setRecordedDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          required
        />
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Performance Metrics *</label>
        <div style={styles.statsList}>
          {stats.map((stat, index) => (
            <div key={index} style={styles.statRow}>
              <div style={{ width: '100%' }}>
                {isMobile && <div style={styles.mobileFieldLabel}>Metric Type</div>}
                <select
                  style={styles.select}
                  value={stat.stat_category_id}
                  onChange={(e) => updateStat(index, 'stat_category_id', e.target.value)}
                  required
                  aria-label={`Metric type ${index + 1}`}
                >
                  {statCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ width: '100%' }}>
                {isMobile && <div style={styles.mobileFieldLabel}>Value</div>}
                <input
                  type="number"
                  step="0.01"
                  style={styles.input}
                  value={stat.value}
                  onChange={(e) => updateStat(index, 'value', e.target.value)}
                  placeholder="Value"
                  required
                  aria-label={`Metric value ${index + 1}`}
                />
              </div>
              <div style={{ width: '100%' }}>
                {isMobile && <div style={styles.mobileFieldLabel}>Unit</div>}
                <input
                  type="text"
                  style={styles.input}
                  value={stat.unit}
                  readOnly
                  placeholder="Unit"
                  aria-label={`Metric unit ${index + 1}`}
                />
              </div>
              {stats.length > 1 && (
                <div style={{ width: isMobile ? '100%' : 'auto' }}>
                  <button
                    type="button"
                    style={{
                      ...styles.smallButton,
                      ...styles.dangerButton,
                      ...(isMobile && { width: '100%', padding: '10px 20px' }),
                    }}
                    onClick={() => removeStatField(index)}
                    aria-label={`Remove metric ${index + 1}`}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          style={{ ...styles.button, ...styles.successButton, ...styles.addButton }}
          onClick={addStatField}
        >
          + Add Another Metric
        </button>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label} htmlFor="notes">
          Notes (optional)
        </label>
        <textarea
          id="notes"
          style={styles.textarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any additional context about these measurements..."
        />
      </div>

      <button
        type="submit"
        style={{ ...styles.button, ...styles.primaryButton }}
        disabled={loading}
      >
        {loading ? 'Recording...' : 'Record Stats'}
      </button>
    </form>
  );
};

export default StatsRecordingForm;
